import { TranslateService } from "@ngx-translate/core";
import {
  BUILT_IN_CARD_FIELD_KEYS,
  BuiltInCardFieldKey,
  CardFieldToggle,
} from "./card-digital.interface";
import { EmployeeSetting } from "../settings/settings.interface";
import { iconKeyForVariable, variableHasContactIcon } from "./variable-icons";

const HIDDEN_IN_CARD_BLOCK = new Set<string>([
  "jobTitle",
  "calendarLink",
  "picture",
  "skypeId",
  "phoneNumber",
  "mobileNumber",
  "www",
  "companyWebsite",
]);

const HIDDEN_KEY_PREFIXES = ["raw", "formatted", "label"] as const;
const CUSTOM_LINK_FIELD_PREFIX = "customLinks:";

export function customLinkFieldKey(index: number): string {
  return `${CUSTOM_LINK_FIELD_PREFIX}${index}`;
}

export function isCustomLinkFieldKey(key: string): boolean {
  return key.startsWith(CUSTOM_LINK_FIELD_PREFIX);
}

function isHiddenCardVariableKey(name: string): boolean {
  if (!name) return true;
  if (HIDDEN_IN_CARD_BLOCK.has(name)) return true;
  if (iconKeyForVariable(name) === "calendar") return true;
  const lower = name.toLowerCase();
  return HIDDEN_KEY_PREFIXES.some((prefix) => lower.startsWith(prefix));
}

function humanizeKey(key: string): string {
  if (!key) return "";
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function translateBuiltInField(
  translate: TranslateService,
  key: BuiltInCardFieldKey,
): string {
  const path = `digitalCards.fields.${key}`;
  const text = translate.instant(path) as string;
  return text && text !== path ? text : humanizeKey(key);
}

function firstLabelByVariableName(
  variables: ReadonlyArray<EmployeeSetting>,
): Map<string, string> {
  const byName = new Map<string, string>();
  for (const row of variables) {
    const name = row.name?.trim();
    const label = row.label?.trim();
    if (name && label && !byName.has(name)) byName.set(name, label);
  }
  return byName;
}

function compareToggleRows(a: CardFieldToggle, b: CardFieldToggle): number {
  const iconA = variableHasContactIcon(a.key);
  const iconB = variableHasContactIcon(b.key);
  if (iconA !== iconB) return iconA ? -1 : 1;
  return a.label.localeCompare(b.label);
}

export function buildCardFieldToggles(
  domainVariables: ReadonlyArray<EmployeeSetting> | null | undefined,
  translate: TranslateService,
): CardFieldToggle[] {
  const list = domainVariables ?? [];
  const builtIn: CardFieldToggle[] = BUILT_IN_CARD_FIELD_KEYS.map(
    (key: BuiltInCardFieldKey): CardFieldToggle => ({
      key,
      label: translateBuiltInField(translate, key),
    }),
  );
  const used = new Set<string>(builtIn.map((row) => row.key));
  const labelsByName = firstLabelByVariableName(list);
  const dynamic: CardFieldToggle[] = [];

  for (const row of list) {
    const key = row.name?.trim() ?? "";
    if (
      !key ||
      used.has(key) ||
      row.entityBound ||
      isHiddenCardVariableKey(key)
    ) {
      continue;
    }
    used.add(key);
    dynamic.push({
      key,
      label: labelsByName.get(key) ?? humanizeKey(key),
    });
  }

  const sortedDynamic: CardFieldToggle[] = [...dynamic].sort(compareToggleRows);
  return [...builtIn, ...sortedDynamic];
}

export function isCardFieldVisible(
  visibleFields: Record<string, boolean> | undefined,
  key: string,
): boolean {
  if (!visibleFields) return true;
  return visibleFields[key] !== false;
}
